/**
 * The type scale, as components rather than remembered class strings.
 *
 * Sizes are the five names in `@theme` (`src/style.css`) and nothing else: a
 * component takes `size="md"`, never a pixel value, so one preference can
 * scale the whole app. De-emphasised text takes the audited `text-muted`
 * token, never an opacity on the text colour, which put it under 4.5:1 on
 * both canvases.
 *
 * @module components/ui/Text
 */

import React, { type ElementType, type ReactNode } from "react";

/**
 * Written out, not interpolated: Tailwind only generates classes it can read.
 * `sm` is drawn with `text-xs` until the scale's own `sm` is moved to its real
 * size, which `style.css` explains.
 */
export const TEXT_SIZE_CLASS = { sm: "text-xs", md: "text-md", lg: "text-lg" } as const;

/**
 * The sizes a glyph or the wordmark takes. Written out, not interpolated, and
 * kept here so a screen never names a `text-*` utility itself.
 */
export const GLYPH_SIZE_CLASS = { lg: "text-lg", xl: "text-xl" } as const;
export type GlyphSize = keyof typeof GLYPH_SIZE_CLASS;

// The brand mark's own size, outside the type scale (#352).
const WORDMARK_SIZE_CLASS = "text-2xl";
export type WordmarkSize = GlyphSize | "wordmark";

export interface GlyphScaleProps {
  children: ReactNode;
  size?: WordmarkSize;
  className?: string;
  "data-testid"?: string;
}

/** A box whose text takes a glyph size, for the wordmark. A screen asks for the size, never the class. */
export function GlyphScale({
  children,
  size = undefined,
  className = "",
  "data-testid": testId,
}: GlyphScaleProps): React.ReactElement {
  let sizeClass = "";
  if (size === "wordmark") {
    sizeClass = WORDMARK_SIZE_CLASS;
  } else if (size) {
    sizeClass = GLYPH_SIZE_CLASS[size];
  }
  return (
    <div className={[sizeClass, className].filter(Boolean).join(" ")} data-testid={testId}>
      {children}
    </div>
  );
}

/** The sizes body text takes. */
export const TEXT_SIZES = ["sm", "md", "lg"] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

export interface TextProps {
  children: ReactNode;
  /** `sm` for a caption or a hint, `md` for body, `lg` for a lead paragraph. */
  size?: TextSize;
  /** `muted` is the audited secondary colour. */
  tone?: "default" | "muted";
  /** Holds the line length near 65 characters, for a paragraph of prose. */
  measure?: boolean;
  as?: "p" | "span" | "div";
  className?: string;
  id?: string;
  "data-testid"?: string;
}

/** Body text at a semantic size and tone. */
export function Text({
  children,
  size = "md",
  tone = "default",
  measure = false,
  as: Tag = "p",
  className = "",
  id = undefined,
  "data-testid": testId,
}: TextProps): React.ReactElement {
  const classes = [
    TEXT_SIZE_CLASS[size],
    tone === "muted" ? "text-muted" : "",
    measure ? "max-w-[65ch]" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Tag id={id} className={classes} data-testid={testId}>
      {children}
    </Tag>
  );
}

export interface EyebrowProps {
  children: ReactNode;
  className?: string;
  /** A heading tag for a label that names a group of controls and is the outline's only name for it. */
  as?: "p" | "span" | "div" | "h3" | "h4" | "h5";
  id?: string;
  "data-testid"?: string;
}

/** The small uppercase label above a title. Never a heading: it names a region, not a section. */
export function Eyebrow({
  children,
  className = "",
  as: Tag = "p",
  id = undefined,
  "data-testid": testId,
}: EyebrowProps): React.ReactElement {
  return (
    <Tag
      id={id}
      className={`${TEXT_SIZE_CLASS.sm} font-bold tracking-[0.09em] text-muted uppercase ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </Tag>
  );
}

/** The sizes a heading takes; one smaller than body text would not read as one. */
export const HEADING_SIZES = ["md", "lg", "xl", "display"] as const;
export type HeadingSize = (typeof HEADING_SIZES)[number];

export interface HeadingProps {
  /** Document level. Keep one `h1` per page and increment by one. */
  level: 1 | 2 | 3 | 4 | 5;
  /** Visual size, when it has to differ from the level for hierarchy reasons. */
  size?: HeadingSize;
  children: ReactNode;
  className?: string;
  id?: string;
  "data-testid"?: string;
}

const HEADING_SIZE: Record<HeadingSize, string> = {
  md: "text-md",
  lg: "text-lg tracking-[-0.01em]",
  xl: "text-xl",
  display: "text-display",
};

const LEVEL_SIZE: Record<HeadingProps["level"], HeadingSize> = {
  1: "display",
  2: "lg",
  3: "md",
  4: "md",
  5: "md",
};

/** A heading whose document level and visual size are set separately. */
export function Heading({
  level,
  size = undefined,
  children,
  className = "",
  id = undefined,
  "data-testid": testId,
}: HeadingProps): React.ReactElement {
  const Tag = `h${level}` as ElementType;
  return (
    <Tag
      id={id}
      className={`font-bold text-balance ${HEADING_SIZE[size ?? LEVEL_SIZE[level]]} ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </Tag>
  );
}

export interface MetricProps {
  children: ReactNode;
  as?: "p" | "span" | "div";
  className?: string;
  "data-testid"?: string;
}

/** A big number: the one place besides a page title that takes the biggest size. */
export function Metric({
  children,
  as: Tag = "p",
  className = "",
  "data-testid": testId,
}: MetricProps): React.ReactElement {
  return (
    <Tag
      className={`text-display font-bold text-base-content ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </Tag>
  );
}

export interface MutedTextProps {
  children: ReactNode;
  /** `caption` is the small step, for the line under a value. */
  size?: "body" | "caption";
  /** Holds the line length near 65 characters for a paragraph of prose. */
  measure?: boolean;
  as?: "p" | "span" | "div";
  className?: string;
  "data-testid"?: string;
}

/**
 * Secondary text at the audited muted token.
 *
 * Kept for the call sites not yet moved to `<Text tone="muted">`, which it
 * renders.
 */
export function MutedText({
  children,
  size = "body",
  measure = false,
  as = "p",
  className = "",
  "data-testid": testId,
}: MutedTextProps): React.ReactElement {
  return (
    <Text
      size={size === "caption" ? "sm" : "md"}
      tone="muted"
      measure={measure}
      as={as}
      className={className}
      data-testid={testId}
    >
      {children}
    </Text>
  );
}

export interface TextLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  "data-testid"?: string;
}

/** A link inside running text, such as a pointer to a control on the same page. */
export function TextLink({ className = "", children, ...rest }: TextLinkProps): React.ReactElement {
  return (
    <a {...rest} className={`link link-primary ${className}`.trim()}>
      {children}
    </a>
  );
}
